/* THE RECORD (Max, 31/10: "một chỗ nào đó trên màn hình có cái đĩa nhạc user click vào có thể nghe được bài hát random mùa noel",
   then "cái đĩa nên ở trên banner nhé"). A little vinyl on Home's Christmas banner, beside the countdown: a press plays a random carol
   and the record spins; another press stops it. It never
   plays by itself (WCAG 1.4.2: sound only on request, and the same button stops it).

   NO RECORDINGS. Every well-known Christmas recording is under copyright, so nothing is downloaded or streamed: the browser plays
   the MELODIES of carols that are long in the public domain (all written before 1900), on a music-box voice made with the Web
   Audio API — a bell-like tone with its overtones, a soft reverb, a quiet bass on the first beat. Shown only in season by
   theme-christmas.css (season.js), with the banner it sits on. */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { readSeason } from './season';

/* note names → frequency */
const SEMI = { C: -9, 'C#': -8, D: -7, 'D#': -6, E: -5, F: -4, 'F#': -3, G: -2, 'G#': -1, A: 0, 'A#': 1, B: 2 };
const freq = (n) => { const m = /^([A-G]#?)(\d)$/.exec(n); return m ? 440 * 2 ** ((SEMI[m[1]] + (Number(m[2]) - 4) * 12) / 12) : 0; };
/* a melody: "note:beats" separated by spaces; R = a rest */
const parse = (s) => s.trim().split(/\s+/).map((t) => { const [n, b] = t.split(':'); return [n, Number(b || 1)]; });

/* Max 31/10: "kiểu vui và nhanh hơn" — only the bright, quick carols, at a dancing tempo; the slow ones (Silent Night, O Christmas
   Tree) are out. `meter` beats a bar, `pickup` beats before the first bar line; the bass and the sleigh bells follow the bar. */
const SONGS = [
  { title: 'Jingle Bells', bpm: 232, meter: 4, pickup: 0, root: 'C3', fifth: 'G2', notes: parse(`
    E4 E4 E4:2 E4 E4 E4:2 E4 G4 C4:1.5 D4:0.5 E4:4 F4 F4 F4:1.5 F4:0.5 F4 E4 E4 E4:0.5 E4:0.5 E4 D4 D4 E4 D4:2 G4:2
    E4 E4 E4:2 E4 E4 E4:2 E4 G4 C4:1.5 D4:0.5 E4:4 F4 F4 F4:1.5 F4:0.5 F4 E4 E4 E4:0.5 E4:0.5 G4 G4 F4 D4 C4:4`) },
  { title: 'We Wish You a Merry Christmas', bpm: 204, meter: 3, pickup: 1, root: 'G2', fifth: 'D3', notes: parse(`
    D4 G4 G4:0.5 A4:0.5 G4:0.5 F#4:0.5 E4 E4 E4 A4 A4:0.5 B4:0.5 A4:0.5 G4:0.5 F#4 D4 D4 B4 B4:0.5 C5:0.5 B4:0.5 A4:0.5 G4 E4 D4:0.5 D4:0.5 E4 A4 F#4 G4:2`) },
  { title: 'Deck the Halls', bpm: 184, meter: 4, pickup: 0, root: 'C3', fifth: 'G2', notes: parse(`
    G4:1.5 F4:0.5 E4 D4 C4 D4 E4 C4 D4:0.5 E4:0.5 F4:0.5 D4:0.5 E4:1.5 D4:0.5 C4 B3 C4:2
    G4:1.5 F4:0.5 E4 D4 C4 D4 E4 C4 D4:0.5 E4:0.5 F4:0.5 D4:0.5 E4:1.5 D4:0.5 C4 B3 C4:2`) },
  { title: 'Joy to the World', bpm: 152, meter: 2, pickup: 0, root: 'C3', fifth: 'G2', notes: parse(`
    C5:2 B4:1.5 A4:0.5 G4:3 F4 E4:2 D4:2 C4:3 G4 A4:3 A4 B4:3 B4 C5:3
    C5 C5 B4 A4 G4 G4:1.5 F4:0.5 E4 C5 C5 B4 A4 G4 G4:1.5 F4:0.5 E4`) },
];

/* one music-box note: the tone and two overtones, a quick strike and a long ring */
const strike = (ctx, out, f, at, len, vol = 0.22) => {
  [[1, 1], [2, 0.32], [3, 0.12]].forEach(([k, g]) => {
    const o = ctx.createOscillator(); const e = ctx.createGain();
    o.type = 'sine'; o.frequency.value = f * k;
    e.gain.setValueAtTime(0.0001, at);
    e.gain.exponentialRampToValueAtTime(vol * g, at + 0.008);
    e.gain.exponentialRampToValueAtTime(0.0001, at + Math.max(0.6, len * 1.6) / k);
    o.connect(e); e.connect(out); o.start(at); o.stop(at + Math.max(0.7, len * 1.7));
  });
};
/* sleigh bells: a short burst of high, ringing noise — on the beat, and softer on the half beat */
const noiseOf = (ctx) => {
  const len = Math.floor(ctx.sampleRate * 0.2); const buf = ctx.createBuffer(1, len, ctx.sampleRate); const d = buf.getChannelData(0);
  for (let i = 0; i < len; i += 1) d[i] = Math.random() * 2 - 1;
  return buf;
};
const jingle = (ctx, out, noise, at, vol) => {
  const src = ctx.createBufferSource(); src.buffer = noise;
  const bp = ctx.createBiquadFilter(); bp.type = 'bandpass'; bp.frequency.value = 7600; bp.Q.value = 1.4;
  const e = ctx.createGain();
  e.gain.setValueAtTime(0.0001, at); e.gain.exponentialRampToValueAtTime(vol, at + 0.004); e.gain.exponentialRampToValueAtTime(0.0001, at + 0.14);
  src.connect(bp); bp.connect(e); e.connect(out); src.start(at); src.stop(at + 0.18);
};
/* a soft room: a decaying noise impulse */
const reverbOf = (ctx) => {
  const len = Math.floor(ctx.sampleRate * 1.6); const buf = ctx.createBuffer(2, len, ctx.sampleRate);
  for (let c = 0; c < 2; c += 1) { const d = buf.getChannelData(c); for (let i = 0; i < len; i += 1) d[i] = (Math.random() * 2 - 1) * (1 - i / len) ** 3; }
  const r = ctx.createConvolver(); r.buffer = buf; return r;
};

export default function XmasMusic() {
  const { t } = useTranslation();
  const [playing, setPlaying] = useState(null);      // the song's title
  const ctxRef = useRef(null); const stopRef = useRef(null); const lastRef = useRef(-1);

  const stop = useCallback(() => {
    if (stopRef.current) { stopRef.current(); stopRef.current = null; }
    setPlaying(null);
  }, []);
  useEffect(() => () => stop(), [stop]);
  /* switching the season off stops the music too */
  useEffect(() => {
    const h = setInterval(() => { if (playing && !readSeason().on) stop(); }, 1500);
    return () => clearInterval(h);
  }, [playing, stop]);

  const play = () => {
    if (playing) { stop(); return; }
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = ctxRef.current || new AC(); ctxRef.current = ctx;
    if (ctx.state === 'suspended') ctx.resume();
    let k = Math.floor(Math.random() * SONGS.length);
    if (k === lastRef.current) k = (k + 1) % SONGS.length;
    lastRef.current = k;
    const song = SONGS[k];
    const master = ctx.createGain(); master.gain.value = 0.55;
    const wet = ctx.createGain(); wet.gain.value = 0.28; const rev = reverbOf(ctx);
    master.connect(ctx.destination); master.connect(rev); rev.connect(wet); wet.connect(ctx.destination);
    const beat = 60 / song.bpm; const t0 = ctx.currentTime + 0.12; let at = t0;
    song.notes.forEach(([n, b]) => { if (n !== 'R') strike(ctx, master, freq(n), at, b * beat); at += b * beat; });
    /* the band under the tune: bass on the bar (root, then the fifth half way through a 4-beat bar or on every other short bar),
       and sleigh bells on every beat and half beat */
    const total = Math.round((at - t0) / beat); const noise = noiseOf(ctx);
    for (let i = 0; i < total * 2; i += 1) {
      const b = i / 2; const inBar = (((b - song.pickup) % song.meter) + song.meter) % song.meter; const bar = Math.floor((b - song.pickup) / song.meter);
      const when = t0 + b * beat;
      if (inBar === 0) strike(ctx, master, freq(song.meter < 4 && bar % 2 ? song.fifth : song.root), when, beat * 1.2, 0.13);
      if (song.meter === 4 && inBar === 2) strike(ctx, master, freq(song.fifth), when, beat * 1.2, 0.11);
      jingle(ctx, master, noise, when, Number.isInteger(b) ? (inBar === 0 ? 0.16 : 0.1) : 0.05);
    }
    const end = setTimeout(() => { setPlaying(null); stopRef.current = null; }, (at - ctx.currentTime + 1.2) * 1000);
    stopRef.current = () => {
      clearTimeout(end);
      try { master.gain.setTargetAtTime(0.0001, ctx.currentTime, 0.08); wet.gain.setTargetAtTime(0.0001, ctx.currentTime, 0.08); } catch (e) { /* closed */ }
      setTimeout(() => { try { master.disconnect(); wet.disconnect(); } catch (e) { /* gone */ } }, 600);
    };
    setPlaying(song.title);
  };

  const label = playing ? t('xShell.xmasMusicStop', { title: playing }) : t('xShell.xmasMusicPlay');
  return (
    <div className={`xmas-music${playing ? ' on' : ''}`}>
      {playing && <span className="xmas-music-now" role="status">♪ {playing}</span>}
      <button type="button" className="xmas-record" onClick={play} aria-pressed={!!playing} aria-label={label} title={label} data-xmas-music>
        <span className="xmas-record-disc" aria-hidden="true" />
      </button>
    </div>
  );
}
